import React from 'react';
import { useWatch } from 'react-hook-form';
import { fmtNum } from '../utils';

// ยอดยกไปงวดหน้า (PayrollCarryover) — บวก/ลบสุทธิที่ยกมาลงงวดนี้ (แยกจากตัวเลขในคอลัมน์ ไม่ merge เข้า field)
const carryDelta = (c) => (Number(c?.addTotal) || 0) - (Number(c?.deductTotal) || 0);
// ยอดติดลบที่งวดนี้ยกไปงวดหน้าแล้ว (PayrollCarryover auto ที่ source_period = งวดนี้, ไม่นับที่ยกเลิก) — บวกคืน
// เข้ายอดสุทธิ/ที่ต้องจ่าย ให้งวดนี้เหลือเท่ายอดที่ HR เลือกจ่าย (0 หรือยอดที่กรอก) ส่วนที่ยกไปหักในงวดหน้าแทน ไม่หักซ้ำสองงวด
export const sumCarryOut = (list) => (list || [])
  .filter((r) => r?.status !== 'cancelled')
  .reduce((sum, r) => sum + (Number(r?.amount) || 0), 0);

// ── NetCell: subscribes only to this employee's form row ──
export const NetCell = React.memo(function NetCell({ control, fi, fields, carryover, carryoverOut }) {
  const rowData = useWatch({ control, name: `salaryData.${fi}` }) || {};
  const f = fields[fi];
  if (!f) return null;
  const sal = parseFloat(rowData?.revenue?.salary ?? f?.totalAmount ?? 0);
  const ot = parseFloat(rowData?.revenue?.overtime || 0);
  const all = parseFloat(rowData?.revenue?.allowances ?? f?.revenue?.allowances ?? f?.total_extra_day ?? 0);
  const fieldAllow = parseFloat(rowData?.revenue?.field_allowance || 0);
  const bon = parseFloat(rowData?.revenue?.bonus || 0);
  const tax = parseFloat(rowData?.expenses?.tax ?? f?.expenses?.tax ?? 0);
  const sso = parseFloat(rowData?.expenses?.sso ?? f?.expenses?.sso ?? 0);
  const late = parseFloat(rowData?.expenses?.late ?? f?.expenses?.late ?? 0);
  const crl = parseFloat(rowData?.expenses?.crl ?? f?.expenses?.crl ?? 0);
  const oth = parseFloat(rowData?.expenses?.other ?? f?.expenses?.other ?? 0);
  const net = sal + ot + all + fieldAllow + bon - tax - sso - late - crl - oth + carryDelta(carryover)
    + sumCarryOut(carryoverOut);
  return <span style={{ color: net >= 0 ? '#059669' : '#DC2626' }}>{fmtNum(net)}</span>;
});
NetCell.displayName = 'NetCell';

// ── TotalNetCell: sum of all rows — isolated from table re-renders ──
export const TotalNetCell = React.memo(function TotalNetCell({ control, fields, fieldIndexMap, timestamp, carryoverByEmpId, carryoverOutByEmpId }) {
  const safeWatch = useWatch({ control, name: 'salaryData' }) || [];
  const totalNet = React.useMemo(() => {
    return (timestamp?.rows || []).reduce((acc, emp) => {
      const fi = fieldIndexMap[String(emp.employee_id)];
      if (fi === undefined) return acc;
      const f = fields[fi];
      const w = safeWatch[fi] || {};
      if (!f) return acc;
      const sal = parseFloat(w?.revenue?.salary ?? f?.totalAmount ?? 0);
      const ot = parseFloat(w?.revenue?.overtime || 0);
      const all = parseFloat(w?.revenue?.allowances ?? f?.revenue?.allowances ?? f?.total_extra_day ?? 0);
      const fieldAllow = parseFloat(w?.revenue?.field_allowance || 0);
      const bon = parseFloat(w?.revenue?.bonus || 0);
      const tax = parseFloat(w?.expenses?.tax ?? f?.expenses?.tax ?? 0);
      const sso = parseFloat(w?.expenses?.sso ?? f?.expenses?.sso ?? 0);
      const late = parseFloat(w?.expenses?.late ?? f?.expenses?.late ?? 0);
      const crl = parseFloat(w?.expenses?.crl ?? f?.expenses?.crl ?? 0);
      const oth = parseFloat(w?.expenses?.other ?? f?.expenses?.other ?? 0);
      const carry = carryDelta(carryoverByEmpId?.[String(emp.employee_id)])
        + sumCarryOut(carryoverOutByEmpId?.[String(emp.employee_id)]);
      return acc + (sal + ot + all + fieldAllow + bon - tax - sso - late - crl - oth + carry);
    }, 0);
  }, [timestamp, fields, safeWatch, fieldIndexMap, carryoverByEmpId, carryoverOutByEmpId]); // eslint-disable-line
  return <span style={{ fontWeight: 700, fontSize: '14px' }}>{fmtNum(totalNet)}</span>;
});
TotalNetCell.displayName = 'TotalNetCell';

// ── ActualNetCell: net based on actual hours (no late deduction) ──
export const ActualNetCell = React.memo(function ActualNetCell({ control, fi, fields, actualSalaryAmt, carryover, carryoverOut }) {
  const rowData = useWatch({ control, name: `salaryData.${fi}` }) || {};
  const f = fields[fi];
  if (!f) return null;
  const ot = parseFloat(rowData?.revenue?.overtime || 0);
  const all = parseFloat(rowData?.revenue?.allowances ?? f?.revenue?.allowances ?? f?.total_extra_day ?? 0);
  const fieldAllow = parseFloat(rowData?.revenue?.field_allowance || 0);
  const bon = parseFloat(rowData?.revenue?.bonus || 0);
  const tax = parseFloat(rowData?.expenses?.tax ?? f?.expenses?.tax ?? 0);
  const sso = parseFloat(rowData?.expenses?.sso ?? f?.expenses?.sso ?? 0);
  const crl = parseFloat(rowData?.expenses?.crl ?? f?.expenses?.crl ?? 0);
  const oth = parseFloat(rowData?.expenses?.other ?? f?.expenses?.other ?? 0);
  const net = (actualSalaryAmt || 0) + ot + all + fieldAllow + bon - tax - sso - crl - oth + carryDelta(carryover)
    + sumCarryOut(carryoverOut);
  return <span style={{ color: net >= 0 ? '#059669' : '#DC2626' }}>{fmtNum(net)}</span>;
});
ActualNetCell.displayName = 'ActualNetCell';

// ── TotalActualNetCell: sum of actual-hour nets ──
export const TotalActualNetCell = React.memo(function TotalActualNetCell({ control, fields, fieldIndexMap, timestamp, empSummaryMap, carryoverByEmpId, carryoverOutByEmpId }) {
  const safeWatch = useWatch({ control, name: 'salaryData' }) || [];
  const total = React.useMemo(() => {
    return (timestamp?.rows || []).reduce((acc, emp) => {
      const fi = fieldIndexMap[String(emp.employee_id)];
      if (fi === undefined) return acc;
      const f = fields[fi];
      const w = safeWatch[fi] || {};
      if (!f) return acc;
      const actualSal = empSummaryMap?.[String(emp.employee_id)]?.actualSalaryAmt || 0;
      const ot = parseFloat(w?.revenue?.overtime || 0);
      const all = parseFloat(w?.revenue?.allowances ?? f?.revenue?.allowances ?? f?.total_extra_day ?? 0);
      const fieldAllow = parseFloat(w?.revenue?.field_allowance || 0);
      const bon = parseFloat(w?.revenue?.bonus || 0);
      const tax = parseFloat(w?.expenses?.tax ?? f?.expenses?.tax ?? 0);
      const sso = parseFloat(w?.expenses?.sso ?? f?.expenses?.sso ?? 0);
      const crl = parseFloat(w?.expenses?.crl ?? f?.expenses?.crl ?? 0);
      const oth = parseFloat(w?.expenses?.other ?? f?.expenses?.other ?? 0);
      const carry = carryDelta(carryoverByEmpId?.[String(emp.employee_id)])
        + sumCarryOut(carryoverOutByEmpId?.[String(emp.employee_id)]);
      return acc + (actualSal + ot + all + fieldAllow + bon - tax - sso - crl - oth + carry);
    }, 0);
  }, [timestamp, fields, safeWatch, fieldIndexMap, empSummaryMap, carryoverByEmpId, carryoverOutByEmpId]); // eslint-disable-line
  return <span style={{ fontWeight: 700, fontSize: '14px' }}>{fmtNum(total)}</span>;
});
TotalActualNetCell.displayName = 'TotalActualNetCell';

// ── CrlDeductCell: read-only mirror of expenses.crl (หัก CRL — เบิกเงินเดือนล่วงหน้า) ──
// ค่ามาจาก /expenses/crl-summary (auto, แก้มือไม่ได้) — HrReportV2 ยัดลง salaryData.${fi}.expenses.crl แล้ว
export const CrlDeductCell = React.memo(function CrlDeductCell({ control, fi, fields }) {
  const rowData = useWatch({ control, name: `salaryData.${fi}` }) || {};
  const f = fields[fi];
  if (!f) return null;
  const crl = parseFloat(rowData?.expenses?.crl ?? f?.expenses?.crl ?? 0);
  return <span style={{ color: crl > 0 ? '#b91c1c' : undefined }}>{fmtNum(crl)}</span>;
});
CrlDeductCell.displayName = 'CrlDeductCell';

// ── TotalCrlDeductCell: sum of expenses.crl across all rows ──
export const TotalCrlDeductCell = React.memo(function TotalCrlDeductCell({ control, fields, fieldIndexMap, timestamp }) {
  const safeWatch = useWatch({ control, name: 'salaryData' }) || [];
  const total = React.useMemo(() => {
    return (timestamp?.rows || []).reduce((acc, emp) => {
      const fi = fieldIndexMap[String(emp.employee_id)];
      if (fi === undefined) return acc;
      const f = fields[fi];
      const w = safeWatch[fi] || {};
      if (!f) return acc;
      return acc + parseFloat(w?.expenses?.crl ?? f?.expenses?.crl ?? 0);
    }, 0);
  }, [timestamp, fields, safeWatch, fieldIndexMap]); // eslint-disable-line
  return <span style={{ fontWeight: 700, fontSize: '14px' }}>{fmtNum(total)}</span>;
});
TotalCrlDeductCell.displayName = 'TotalCrlDeductCell';

// ── CarryoverChip: ป้ายในเซลล์หมายเหตุ — "ยกมา" (เข้างวดนี้) / "ยกไป" (auto ไปงวดหน้า) ──
// กดเปิด dialog จัดการยอดยกไปงวดหน้าของพนักงานคนนั้น
export const CarryoverChip = React.memo(function CarryoverChip({ incoming, outgoing, onClick }) {
  const inNet = (Number(incoming?.addTotal) || 0) - (Number(incoming?.deductTotal) || 0);
  const hasIn = (incoming?.items || []).length > 0;
  const outAmt = (outgoing || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const hasOut = outAmt > 0;
  let label = '+ ยกยอด';
  let color = '#94a3b8';
  if (hasIn) {
    label = `ยกมา ${inNet >= 0 ? '+' : '−'}${fmtNum(Math.abs(inNet))}`;
    color = inNet >= 0 ? '#15803d' : '#b91c1c';
  } else if (hasOut) {
    label = `ยกไป −${fmtNum(outAmt)}`;
    color = '#b45309';
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      title="จัดการยอดยกไปงวดหน้า"
      style={{
        alignSelf: 'flex-start',
        border: `1px dashed ${color}`,
        color,
        background: hasIn || hasOut ? '#fff' : 'transparent',
        borderRadius: 4,
        padding: '0 4px',
        fontSize: '0.62rem',
        lineHeight: '14px',
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: 'inherit',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
});
CarryoverChip.displayName = 'CarryoverChip';

// ── SsoIncomeCell: read-only mirror of expenses.sso (employer's SSO contribution) ──
export const SsoIncomeCell = React.memo(function SsoIncomeCell({ control, fi, fields }) {
  const rowData = useWatch({ control, name: `salaryData.${fi}` }) || {};
  const f = fields[fi];
  if (!f) return null;
  const sso = parseFloat(rowData?.expenses?.sso ?? f?.expenses?.sso ?? 0);
  return <span>{fmtNum(sso)}</span>;
});
SsoIncomeCell.displayName = 'SsoIncomeCell';

// ── CompanyCostCell: sum of all income (green) columns — true cost to company ──
export const CompanyCostCell = React.memo(function CompanyCostCell({ control, fi, fields, actualSalaryAmt, useActualSalary }) {
  const rowData = useWatch({ control, name: `salaryData.${fi}` }) || {};
  const f = fields[fi];
  if (!f) return null;
  const sal = useActualSalary
    ? (actualSalaryAmt || 0)
    : parseFloat(rowData?.revenue?.salary ?? f?.totalAmount ?? 0);
  const ot = parseFloat(rowData?.revenue?.overtime || 0);
  const all = parseFloat(rowData?.revenue?.allowances ?? f?.revenue?.allowances ?? f?.total_extra_day ?? 0);
  const fieldAllow = parseFloat(rowData?.revenue?.field_allowance || 0);
  const bon = parseFloat(rowData?.revenue?.bonus || 0);
  const sso = parseFloat(rowData?.expenses?.sso ?? f?.expenses?.sso ?? 0);
  const cost = sal + ot + all + fieldAllow + bon + sso;
  return <span style={{ fontWeight: 700 }}>{fmtNum(cost)}</span>;
});
CompanyCostCell.displayName = 'CompanyCostCell';

// ── TotalSsoIncomeCell: sum of the SSO income-mirror column across rows ──
export const TotalSsoIncomeCell = React.memo(function TotalSsoIncomeCell({ control, fields, fieldIndexMap, timestamp }) {
  const safeWatch = useWatch({ control, name: 'salaryData' }) || [];
  const total = React.useMemo(() => {
    return (timestamp?.rows || []).reduce((acc, emp) => {
      const fi = fieldIndexMap[String(emp.employee_id)];
      if (fi === undefined) return acc;
      const f = fields[fi];
      const w = safeWatch[fi] || {};
      if (!f) return acc;
      const sso = parseFloat(w?.expenses?.sso ?? f?.expenses?.sso ?? 0);
      return acc + sso;
    }, 0);
  }, [timestamp, fields, safeWatch, fieldIndexMap]); // eslint-disable-line
  return <span style={{ fontWeight: 700, fontSize: '14px' }}>{fmtNum(total)}</span>;
});
TotalSsoIncomeCell.displayName = 'TotalSsoIncomeCell';

// ── TotalAllowanceCell: sum of เบี้ยเลี้ยง (revenue.allowances) across rows ──
export const TotalAllowanceCell = React.memo(function TotalAllowanceCell({ control, fields, fieldIndexMap, timestamp }) {
  const safeWatch = useWatch({ control, name: 'salaryData' }) || [];
  const total = React.useMemo(() => {
    return (timestamp?.rows || []).reduce((acc, emp) => {
      const fi = fieldIndexMap[String(emp.employee_id)];
      if (fi === undefined) return acc;
      const f = fields[fi];
      const w = safeWatch[fi] || {};
      if (!f) return acc;
      const all = parseFloat(w?.revenue?.allowances ?? f?.revenue?.allowances ?? f?.total_extra_day ?? 0);
      return acc + all;
    }, 0);
  }, [timestamp, fields, safeWatch, fieldIndexMap]); // eslint-disable-line
  return <span style={{ fontWeight: 700, fontSize: '14px' }}>{fmtNum(total)}</span>;
});
TotalAllowanceCell.displayName = 'TotalAllowanceCell';

// ── TotalFieldAllowanceCell: sum of ค่าออกสนาม (revenue.field_allowance) across rows ──
export const TotalFieldAllowanceCell = React.memo(function TotalFieldAllowanceCell({ control, fields, fieldIndexMap, timestamp }) {
  const safeWatch = useWatch({ control, name: 'salaryData' }) || [];
  const total = React.useMemo(() => {
    return (timestamp?.rows || []).reduce((acc, emp) => {
      const fi = fieldIndexMap[String(emp.employee_id)];
      if (fi === undefined) return acc;
      const f = fields[fi];
      const w = safeWatch[fi] || {};
      if (!f) return acc;
      const fieldAllow = parseFloat(w?.revenue?.field_allowance || 0);
      return acc + fieldAllow;
    }, 0);
  }, [timestamp, fields, safeWatch, fieldIndexMap]); // eslint-disable-line
  return <span style={{ fontWeight: 700, fontSize: '14px' }}>{fmtNum(total)}</span>;
});
TotalFieldAllowanceCell.displayName = 'TotalFieldAllowanceCell';

// ── TotalBonusCell: sum of เงินได้อื่นๆ (revenue.bonus) across rows ──
export const TotalBonusCell = React.memo(function TotalBonusCell({ control, fields, fieldIndexMap, timestamp }) {
  const safeWatch = useWatch({ control, name: 'salaryData' }) || [];
  const total = React.useMemo(() => {
    return (timestamp?.rows || []).reduce((acc, emp) => {
      const fi = fieldIndexMap[String(emp.employee_id)];
      if (fi === undefined) return acc;
      const f = fields[fi];
      const w = safeWatch[fi] || {};
      if (!f) return acc;
      const bon = parseFloat(w?.revenue?.bonus || 0);
      return acc + bon;
    }, 0);
  }, [timestamp, fields, safeWatch, fieldIndexMap]); // eslint-disable-line
  return <span style={{ fontWeight: 700, fontSize: '14px' }}>{fmtNum(total)}</span>;
});
TotalBonusCell.displayName = 'TotalBonusCell';

// ── TotalTaxCell: sum of หักภาษี (expenses.tax) across rows ──
export const TotalTaxCell = React.memo(function TotalTaxCell({ control, fields, fieldIndexMap, timestamp }) {
  const safeWatch = useWatch({ control, name: 'salaryData' }) || [];
  const total = React.useMemo(() => {
    return (timestamp?.rows || []).reduce((acc, emp) => {
      const fi = fieldIndexMap[String(emp.employee_id)];
      if (fi === undefined) return acc;
      const f = fields[fi];
      const w = safeWatch[fi] || {};
      if (!f) return acc;
      const tax = parseFloat(w?.expenses?.tax ?? f?.expenses?.tax ?? 0);
      return acc + tax;
    }, 0);
  }, [timestamp, fields, safeWatch, fieldIndexMap]); // eslint-disable-line
  return <span style={{ fontWeight: 700, fontSize: '14px' }}>{fmtNum(total)}</span>;
});
TotalTaxCell.displayName = 'TotalTaxCell';

// ── TotalOtherDeductCell: sum of หักอื่นๆ (expenses.other) across rows ──
export const TotalOtherDeductCell = React.memo(function TotalOtherDeductCell({ control, fields, fieldIndexMap, timestamp }) {
  const safeWatch = useWatch({ control, name: 'salaryData' }) || [];
  const total = React.useMemo(() => {
    return (timestamp?.rows || []).reduce((acc, emp) => {
      const fi = fieldIndexMap[String(emp.employee_id)];
      if (fi === undefined) return acc;
      const f = fields[fi];
      const w = safeWatch[fi] || {};
      if (!f) return acc;
      const oth = parseFloat(w?.expenses?.other ?? f?.expenses?.other ?? 0);
      return acc + oth;
    }, 0);
  }, [timestamp, fields, safeWatch, fieldIndexMap]); // eslint-disable-line
  return <span style={{ fontWeight: 700, fontSize: '14px' }}>{fmtNum(total)}</span>;
});
TotalOtherDeductCell.displayName = 'TotalOtherDeductCell';

// ── TotalCompanyCostCell: sum of company cost (all income columns) across rows ──
export const TotalCompanyCostCell = React.memo(function TotalCompanyCostCell({ control, fields, fieldIndexMap, timestamp, empSummaryMap, useActualSalary }) {
  const safeWatch = useWatch({ control, name: 'salaryData' }) || [];
  const total = React.useMemo(() => {
    return (timestamp?.rows || []).reduce((acc, emp) => {
      const fi = fieldIndexMap[String(emp.employee_id)];
      if (fi === undefined) return acc;
      const f = fields[fi];
      const w = safeWatch[fi] || {};
      if (!f) return acc;
      const sal = useActualSalary
        ? (empSummaryMap?.[String(emp.employee_id)]?.actualSalaryAmt || 0)
        : parseFloat(w?.revenue?.salary ?? f?.totalAmount ?? 0);
      const ot = parseFloat(w?.revenue?.overtime || 0);
      const all = parseFloat(w?.revenue?.allowances ?? f?.revenue?.allowances ?? f?.total_extra_day ?? 0);
      const fieldAllow = parseFloat(w?.revenue?.field_allowance || 0);
      const bon = parseFloat(w?.revenue?.bonus || 0);
      const sso = parseFloat(w?.expenses?.sso ?? f?.expenses?.sso ?? 0);
      return acc + (sal + ot + all + fieldAllow + bon + sso);
    }, 0);
  }, [timestamp, fields, safeWatch, fieldIndexMap, empSummaryMap, useActualSalary]); // eslint-disable-line
  return <span style={{ fontWeight: 700, fontSize: '14px' }}>{fmtNum(total)}</span>;
});
TotalCompanyCostCell.displayName = 'TotalCompanyCostCell';

// ── FieldAllowanceBadge: absolute badge on name cell ──
export const FieldAllowanceBadge = React.memo(function FieldAllowanceBadge({ faEnabled, faAmt }) {
  if (!faEnabled) return null;
  return (
    <div
      style={{
        position: 'absolute',
        top: 2,
        right: 4,
        background: '#dcfce7',
        border: '1px solid #86efac',
        borderRadius: 4,
        padding: '1px 5px',
        fontSize: '10px',
        fontWeight: 700,
        color: '#15803d',
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        zIndex: 1,
      }}
    >
      +{faAmt > 0 ? Number(faAmt).toLocaleString('th-TH', { maximumFractionDigits: 0 }) : '?'}
    </div>
  );
});
FieldAllowanceBadge.displayName = 'FieldAllowanceBadge';

// ── ManualDot: orange dot when a field has been manually edited by HR ──
export const ManualDot = React.memo(function ManualDot({ control, fi, fieldName }) {
  const manualFields = useWatch({ control, name: `salaryData.${fi}.manual_fields` }) || [];
  if (!manualFields.includes(fieldName)) return null;
  return (
    <span
      title="แก้ไขโดย HR"
      style={{
        display: 'inline-block',
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: '#f97316',
        flexShrink: 0,
        marginLeft: 1,
        alignSelf: 'center',
      }}
    />
  );
});
ManualDot.displayName = 'ManualDot';
